import React, { useState } from 'react';
import { Form, Input, Button, Typography, Select, message, Divider } from 'antd';
import { LockOutlined, MobileOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import logoTitle from '../../icons/logo-title.png';
import iconLogo  from '../../icons/logo.png';
import { loginThunk } from '../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const COUNTRY_CODES = [
  { value: '+91', label: '🇮🇳 +91' },
  { value: '+1',  label: '🇺🇸 +1'  },
  { value: '+44', label: '🇬🇧 +44' },
];

const FEATURES = [
  'Track crop expenses & income',
  'Manage farm accounts easily',
  'Instant profit & loss reports',
  'Works in multiple languages',
];

function HeroPanel() {
  return (
    <div className="auth-hero" style={{
      flex: 1,
      background: 'linear-gradient(rgba(10,40,18,0.62), rgba(10,40,18,0.62)), url(/rightbg.jpeg) center/cover no-repeat',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: '48px 56px',
    }}>
      {/* Dark overlay so text stays readable */}
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: -100, right: -100, width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '30%', left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

      {/* Logo */}
      <div style={{
        background: '#fff',
        borderRadius: 24,
        padding: 18,
        marginBottom: 28,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <img src={iconLogo} alt="logo" style={{ width: 72, height: 72, display: 'block', objectFit: 'contain' }} />
      </div>

      <h1 style={{ color: '#fff', fontSize: 36, fontWeight: 800, margin: '0 0 10px', textAlign: 'center', letterSpacing: -0.5 }}>
        Kheti Hisaab
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 17, textAlign: 'center', margin: '0 0 48px', maxWidth: 320 }}>
        Smart farm accounting for every farmer
      </p>

      {/* Feature list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 340 }}>
        {FEATURES.map((f) => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <CheckCircleFilled style={{ fontSize: 22, color: '#52c41a', flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 15, fontWeight: 500 }}>{f}</span>
          </div>
        ))}
      </div>

      {/* Bottom badge */}
      <div style={{
        position: 'absolute', bottom: 32,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 20, padding: '8px 20px',
        color: 'rgba(255,255,255,0.6)', fontSize: 12,
      }}>
        Trusted by farmers across India 🇮🇳
      </div>
    </div>
  );
}

export default function Login() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { isLoading } = useSelector((s) => s.auth);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [countryCode, setCountryCode] = useState('+91');

  const handleLogin = async (values) => {
    const result = await dispatch(loginThunk({
      mobile:       values.mobile,
      country_code: countryCode,
      password:     values.password,
      fcmToken:     '',
      is_from_web:  true,
    }));
    if (loginThunk.fulfilled.match(result)) {
      message.success('Welcome back!');
      navigate('/farms');
    } else {
      const p = result.payload;
      if (p?.status === 403) {
        message.error('Your plan does not include web access. Please upgrade to Premium or Pro.');
      } else {
        message.error(p?.message || 'Login failed');
      }
    }
  };

  return (
    <>
      <style>{`
        .auth-hero { display: flex !important; }
        .auth-left { width: 480px; min-width: 340px; }
        @media (max-width: 768px) {
          .auth-hero { display: none !important; }
          .auth-left { width: 100% !important; min-width: unset !important; }
        }
      `}</style>

      <div style={{ height: '100vh', display: 'flex', overflow: 'hidden' }}>

        {/* ── Left: Form ── */}
        <div className="auth-left" style={{
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '48px 52px',
          background: '#fff',
        }}>
          {/* Brand */}
          <div style={{ marginBottom: 36 }}>
            <img src={logoTitle} alt="Kheti Hisaab" style={{ height: 72, width: 'auto', marginBottom: 20, display: 'block' }} />
            <Title level={2} style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#141414' }}>Welcome back</Title>
            <Text type="secondary" style={{ fontSize: 15 }}>Sign in to your account</Text>
          </div>

          <Form form={form} layout="vertical" onFinish={handleLogin} size="large">
            <Form.Item
              label={t('mobile')}
              name="mobile"
              rules={[{ required: true, message: 'Enter mobile number' }, { len: 10, message: 'Enter 10-digit number' }]}
            >
              <Input
                prefix={
                  <Select
                    value={countryCode}
                    onChange={setCountryCode}
                    bordered={false}
                    style={{ width: 100, marginLeft: -8, height: 46 }}
                    options={COUNTRY_CODES}
                  />
                }
                suffix={<MobileOutlined style={{ color: '#ccc' }} />}
                placeholder="9876543210"
                maxLength={10}
                inputMode="numeric"
                style={{ borderRadius: 10, height: 48 }}
              />
            </Form.Item>

            <Form.Item
              label={t('password')}
              name="password"
              rules={[{ required: true, message: 'Enter your password' }]}
              style={{ marginBottom: 8 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#ccc' }} />}
                placeholder="Enter your password"
                style={{ borderRadius: 10, height: 48 }}
              />
            </Form.Item>

            <div style={{ textAlign: 'right', marginBottom: 24 }}>
              <Link to="/forgot-password" style={{ color: '#1a6b2a', fontSize: 13, fontWeight: 500 }}>
                Forgot Password?
              </Link>
            </div>

            <Button
              type="primary" htmlType="submit" block loading={isLoading}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a', height: 48, fontSize: 16, borderRadius: 10, fontWeight: 600 }}
            >
              Sign In
            </Button>
          </Form>

          <Divider style={{ margin: '28px 0' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>or</Text>
          </Divider>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Don't have an account? </Text>
            <Link to="/signup" style={{ color: '#1a6b2a', fontWeight: 600 }}>{t('signup')}</Link>
          </div>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <Link to="/select-language" style={{ color: '#aaa', fontSize: 13 }}>🌐 Change Language</Link>
          </div>
        </div>

        {/* ── Right: Hero ── */}
        <HeroPanel />
      </div>
    </>
  );
}
